import { makeStyles } from 'tss-react/mui';

export default makeStyles()(theme => ({
  paper: {
    maxWidth: theme.spacing(90)
  },
  header: {
    padding: theme.spacing(3, 3, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  eyebrow: {
    display: 'block',
    color: theme.palette.text.primary,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5)
  },
  title: {
    color: theme.palette.text.secondary,
    fontWeight: 500
  },
  subtitle: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5)
  },
  content: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    // MUI zeroes the top padding of a DialogContent that directly follows a
    // DialogTitle (`.MuiDialogTitle-root + .MuiDialogContent-root`), which
    // wins on specificity over the single class above and leaves the cards
    // flush against the header divider. Restored at matching specificity.
    '.MuiDialogTitle-root + &': {
      paddingTop: theme.spacing(3)
    }
  },
  summary: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '1fr 1fr'
    }
  },
  card: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#F7F8FA',
    // Column flow so the three bands line up across both cards: an
    // equal-height head, the amount right after it, and the hint pinned to
    // the bottom by the auto margin below.
    display: 'flex',
    flexDirection: 'column'
  },
  // Neutral on purpose: the two cards are the two sides of one comparison,
  // not a good and a bad outcome. Semantic color here read as a verdict the
  // number does not support.
  cardAlt: {
    backgroundColor: theme.palette.badge.neutral.bg
  },
  cardLabel: {
    color: theme.palette.text.primary,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 500,
    lineHeight: 1.5,
    // Two label lines' worth, always: one card's label wraps and the other's
    // does not, and without a reserved height the amounts below land on
    // different baselines and the side-by-side comparison stops reading.
    minHeight: theme.spacing(4.5)
  },
  cardAmount: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
    margin: theme.spacing(1.5, 0, 1)
  },
  cardHint: {
    color: theme.palette.text.primary,
    display: 'block',
    // Bottom-pinned so hints of different lengths still sit on one line.
    marginTop: 'auto'
  },
  rows: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`
    },
    // The last row is the result of the two above it; a heavier rule reads as
    // the line of a subtraction rather than one more separator.
    '&:last-child': {
      borderTop: `2px solid ${theme.palette.primary.main}`
    }
  },
  rowLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 500
  },
  rowHint: {
    color: theme.palette.text.primary,
    display: 'block',
    marginTop: theme.spacing(0.25)
  },
  rowValue: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  // The difference is the result of the two rows above it, so it carries more
  // weight than them — but no semantic color: it is neither a gain nor a loss,
  // just the part of the grace-period gap still open at the payoff.
  rowValueTotal: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  criteria: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.badge.info.bg,
    color: theme.palette.badge.info.text
  },
  criteriaIcon: {
    flex: 'none',
    marginTop: theme.spacing(0.25)
  },
  criteriaTitle: {
    fontWeight: 500,
    display: 'block',
    marginBottom: theme.spacing(0.5)
  },
  actions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  registered: {
    color: theme.palette.text.primary
  }
}));
